// src/Pages/Verify/VerifyPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Stack,
  LinearProgress,
  Chip,
  CardMedia,
  Divider,
  useTheme,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VerifiedIcon from "@mui/icons-material/Verified";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDispatch, useSelector } from "react-redux";
import {
  submitKyc,
  fetchMyKyc,
  selectMyKyc,
  selectKycLoading,
  selectKycError,
  clearKycError,
} from "../../store/Slices/kycSlice";

// اختيارية: في حال عندك userSlice يحوي بيانات المستخدم وحالته
const selectUserData = (s) => s.user?.data || null;

export default function VerifyPage() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const mine = useSelector(selectMyKyc);
  const loading = useSelector(selectKycLoading);
  const error = useSelector(selectKycError);
  const userData = useSelector(selectUserData);

  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [doneMsg, setDoneMsg] = useState("");

  // عند فتح الصفحة: نجلب آخر حالة KYC
  useEffect(() => {
    dispatch(fetchMyKyc());
  }, [dispatch]);

  // نستنتج الحالة الحالية من الطلب أو من حالة المستخدم كاحتياط
  const statusRaw = mine?.status || userData?.kyc_status || "unverified";
  const status = String(statusRaw).toLowerCase();

  const isPending = status === "pending";
  const isApproved = status === "approved" || status === "verified";
  const isRejected = status === "rejected";
  const isUnverified = !isPending && !isApproved && !isRejected;

  // الـ Stepper:
  // 0 = Unverified
  // 1 = Under Review
  // 2 = Verified
  const activeStep = isApproved ? 2 : isPending ? 1 : 0;
  const steps = ["Unverified", "Under Review", "Verified"];

  // أثناء المراجعة (pending) نعمل polling تلقائي لتحديث الحالة
  useEffect(() => {
    if (!isPending) return;
    const id = setInterval(() => {
      dispatch(fetchMyKyc());
      // لو عندك thunk fetchUser() تقدر تناديه هنا أيضاً لتحديث userData
      // dispatch(fetchUser());
    }, 10000);
    return () => clearInterval(id);
  }, [isPending, dispatch]);

  // لا نسمح بالرفع عندما:
  // - الطلب تحت المراجعة
  // - الحساب أصبح verified
  // - نحن حالياً نعمل loading
  const disabledUpload = isPending || isApproved || loading;

  const onSubmit = async () => {
    if (!front || !back || disabledUpload) return;
    const fd = new FormData();
    fd.append("front", front);
    fd.append("back", back);

    setDoneMsg("");
    const res = await dispatch(submitKyc(fd));

    if (submitKyc.fulfilled.match(res)) {
      setDoneMsg(
        "✅ Your verification request has been submitted successfully. It will be reviewed within 24 hours."
      );
      dispatch(fetchMyKyc());
      setFront(null);
      setBack(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // الشارة (Chip) التي تعرض حالة المستخدم
  const statusChip = useMemo(() => {
    if (isApproved)
      return <Chip icon={<VerifiedIcon />} color="success" label="Verified" />;
    if (isPending)
      return (
        <Chip
          icon={<HourglassBottomIcon />}
          color="warning"
          label="Under Review"
        />
      );
    if (isRejected)
      return <Chip icon={<CancelIcon />} color="error" label="Rejected" />;
    return <Chip variant="outlined" label="Unverified" />;
  }, [isApproved, isPending, isRejected]);

  /**
   * الخلفية الديناميكية المتجاوبة مع الثيم:
   * - في الوضع الداكن: تدرج بين background.default و background.paper مع هالات خفيفة فاتحة
   * - في الوضع الفاتح: تدرج بين الرمادي الفاتح والأبيض مع هالات خفيفة داكنة
   *
   * نستخدم useMemo حتى لا نعيد الحساب إلا عند تغير الـ theme.mode
   */
  const bg = useMemo(() => {
    const base = theme.palette.background.default;
    const paper = theme.palette.background.paper;

    if (theme.palette.mode === "dark") {
      return `linear-gradient(180deg, ${base} 0%, ${paper} 100%),
              radial-gradient(800px 400px at 20% 0%, rgba(255,255,255,0.05), transparent 70%),
              radial-gradient(600px 300px at 80% 30%, rgba(255,255,255,0.04), transparent 70%)`;
    }

    return `linear-gradient(180deg, ${base} 0%, ${paper} 100%),
            radial-gradient(800px 400px at 20% 0%, rgba(0,0,0,0.04), transparent 70%),
            radial-gradient(600px 300px at 80% 30%, rgba(0,0,0,0.03), transparent 70%)`;
  }, [
    theme.palette.mode,
    theme.palette.background.default,
    theme.palette.background.paper,
  ]);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background: bg,
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 5 },
        display: "flex",
        justifyContent: "center",
        transition: "background 0.3s ease",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 980 }}>
        {/* Header: العنوان + حالة الحساب */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" fontWeight={600} color="text.primary">
            Identity Verification (KYC)
          </Typography>

          {statusChip}
        </Stack>

        {/* شريط الخطوات (Stepper) */}
        <Card
          sx={{
            mb: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "none",
            backgroundColor: theme.palette.background.paper,
            backdropFilter: "blur(6px)",
          }}
        >
          <CardContent sx={{ py: 2.5 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* لو فيه تحميل عام */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* رسالة نجاح بعد إرسال الطلب */}
        {doneMsg && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {doneMsg}
          </Alert>
        )}

        {/* حالة Pending */}
        {isPending && (
          <Alert
            severity="info"
            icon={<HourglassBottomIcon />}
            sx={{
              mb: 3,
              fontSize: "0.95rem",
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.03)",
              borderRadius: 2,
            }}
          >
            Your verification is currently <strong>under review</strong>. This
            may take up to <strong>24 hours</strong>.
            <br />
            Please wait until the verification is complete before submitting
            again.
          </Alert>
        )}

        {/* حالة Approved */}
        {isApproved && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ✅ Your identity has been successfully verified. You now have full
            withdrawal access.
          </Alert>
        )}

        {/* حالة Rejected */}
        {isRejected && (
          <Alert severity="error" sx={{ mb: 3 }}>
            ❌ Your verification request was rejected. Please re-upload clearer
            photos with all information visible.
          </Alert>
        )}

        {/* رسالة الخطأ العامة القادمة من الـ slice */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => dispatch(clearKycError())}
          >
            {String(error)}
          </Alert>
        )}

        {/* بطاقة الرفع */}
        <Card
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: theme.palette.background.paper,
            backdropFilter: "blur(6px)",
          }}
        >
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 500 }}>
              Upload your government-issued ID
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <UploadSlot
                title="Front Side"
                description="Ensure the front side shows your face, name, and ID number clearly."
                file={front}
                setFile={setFront}
                disabled={disabledUpload}
              />

              <UploadSlot
                title="Back Side"
                description="Ensure the back side shows all text, barcodes, and seals clearly."
                file={back}
                setFile={setBack}
                disabled={disabledUpload}
              />
            </Stack>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={onSubmit}
                startIcon={<UploadFileIcon />}
                disabled={!front || !back || disabledUpload}
                sx={{
                  textTransform: "none",
                  px: 3,
                  fontWeight: 600,
                }}
              >
                {isPending ? "Awaiting Review" : "Submit Verification"}
              </Button>

              <Button
                variant="text"
                onClick={() => {
                  setFront(null);
                  setBack(null);
                }}
                disabled={disabledUpload}
              >
                Clear
              </Button>
            </Stack>

            <Alert
              severity="info"
              sx={{
                mt: 3,
                borderRadius: 2,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "rgba(0,0,0,0.03)",
              }}
            >
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                • You can submit your ID <strong>only once at a time</strong>.
                Please wait while your current request is being reviewed.
                <br />• Make sure your photos are well-lit, focused, and contain
                all visible details. Avoid glare or cropped edges.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

// =====================
// مكوّن رفع كل جهة من الهوية
// =====================
function UploadSlot({ title, description, file, setFile, disabled }) {
  const [preview, setPreview] = useState("");

  useEffect(() => {
    if (!file) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  return (
    <Card
      sx={{
        flex: 1,
        borderRadius: 2,
        border: "1px dashed",
        borderColor: "divider",
        minHeight: 300,
        bgcolor: "background.default",
      }}
    >
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 500 }}>
          {title}
        </Typography>

        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          {description}
        </Typography>

        <Box
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            height: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper",
          }}
        >
          {preview ? (
            <CardMedia
              component="img"
              image={preview}
              alt={title}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Box sx={{ textAlign: "center", p: 2, color: "text.disabled" }}>
              <Typography variant="caption">No file selected</Typography>
            </Box>
          )}
        </Box>

        <Button
          variant="outlined"
          component="label"
          disabled={disabled}
          sx={{ mt: 1.5, textTransform: "none" }}
        >
          {file ? "Change File" : "Choose File"}
          <input hidden type="file" accept="image/*" onChange={onPick} />
        </Button>
      </CardContent>
    </Card>
  );
}
